import React, { useCallback, useRef } from "react";
import { QuestionItem } from "../../../types/form";

const WEIGHT_KEYS = ["pounds_1", "pounds_2", "pounds_3"] as const;

export type WeightDigitKey = (typeof WEIGHT_KEYS)[number];

interface WeightFieldRendererProps {
  question: QuestionItem;
  inputName: string;
  weightAnswer: Record<string, any>;
  handleCompositeFieldChange: (
    inputName: string,
    compositeAnswer: Record<string, any>,
    fieldKey: string,
    value: any,
  ) => void;
  readOnly?: boolean;
}

function digitFromAnswer(ans: Record<string, any>, key: WeightDigitKey): string {
  const v = ans?.[key];
  if (v == null || v === "") return "";
  const d = String(v).replace(/\D/g, "").slice(-1);
  return d;
}

const WeightFieldRenderer: React.FC<WeightFieldRendererProps> = ({
  question,
  inputName,
  weightAnswer,
  handleCompositeFieldChange,
  readOnly = false,
}) => {
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const setDigit = useCallback(
    (key: WeightDigitKey, raw: string) => {
      const digit = raw.replace(/\D/g, "").slice(0, 1);
      handleCompositeFieldChange(inputName, weightAnswer, key, digit);
      return digit;
    },
    [handleCompositeFieldChange, inputName, weightAnswer],
  );

  const focusAt = (idx: number) => {
    const el = inputRefs.current[idx];
    if (el) {
      el.focus();
      el.select();
    }
  };

  const onChangeAt = (idx: number, key: WeightDigitKey, e: React.ChangeEvent<HTMLInputElement>) => {
    const digit = setDigit(key, e.target.value);
    if (digit && idx < WEIGHT_KEYS.length - 1) {
      focusAt(idx + 1);
    }
  };

  const onKeyDownAt = (idx: number, key: WeightDigitKey, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      const cur = digitFromAnswer(weightAnswer, key);
      if (!cur && idx > 0) {
        e.preventDefault();
        focusAt(idx - 1);
      }
    }
    if (e.key === "ArrowLeft" && idx > 0) {
      e.preventDefault();
      focusAt(idx - 1);
    }
    if (e.key === "ArrowRight" && idx < WEIGHT_KEYS.length - 1) {
      e.preventDefault();
      focusAt(idx + 1);
    }
  };

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text") || "";
    const digits = text.replace(/\D/g, "").slice(0, 3);
    if (!digits) return;
    WEIGHT_KEYS.forEach((k, i) => {
      handleCompositeFieldChange(inputName, weightAnswer, k, digits[i] ?? "");
    });
    const lastIdx = Math.min(digits.length, WEIGHT_KEYS.length) - 1;
    if (lastIdx >= 0) focusAt(lastIdx);
  };

  const unit =
    (question.validationRules?.unit as string | undefined) ||
    (question.originalQuestion?.validationRules?.unit as string | undefined) ||
    "lbs";

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2" role="group" aria-label={`Weight in ${unit}, one digit per box`}>
          {WEIGHT_KEYS.map((key, idx) => (
            <input
              key={key}
              ref={(el) => {
                inputRefs.current[idx] = el;
              }}
              type="text"
              inputMode="numeric"
              autoComplete="off"
              name={`${inputName}_${key}`}
              id={`${inputName}_${key}`}
              value={digitFromAnswer(weightAnswer, key)}
              onChange={(e) => onChangeAt(idx, key, e)}
              onKeyDown={(e) => onKeyDownAt(idx, key, e)}
              onPaste={idx === 0 ? onPaste : undefined}
              disabled={readOnly}
              maxLength={1}
              aria-label={`Weight digit ${idx + 1} of 3 (${unit})`}
              className="w-11 h-12 sm:w-12 sm:h-12 text-center text-xl font-semibold border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
            />
          ))}
        </div>
        <span className="text-sm text-[#1B365D]/80">{unit}</span>
      </div>
      <p className="text-xs text-[#1B365D]/70">
        Enter one digit per box (use a leading zero if needed, e.g. 095).
      </p>
    </div>
  );
};

export default WeightFieldRenderer;
